import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '@/supabase/client'
import { useAuth } from '@/lib/auth-context'

// Storybooks cost 18 Tower Credits (TC) each
const STORYBOOK_TC_COST = 18
// Teachers get 1 free storybook per week
const FREE_PER_WEEK = 1

// Get the start of the current week (Monday 00:00:00 UTC)
function getWeekStart(): string {
  const now = new Date()
  const utc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  const day = now.getUTCDay()
  const diff = (day + 6) % 7 // Days since Monday
  const weekStart = new Date(utc - diff * 24 * 60 * 60 * 1000)
  return weekStart.toISOString()
}

// Get formatted week range (Monday - Sunday) for display
export function getWeekRange(): { start: string; end: string } {
  const now = new Date()
  const utc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  const day = now.getUTCDay()
  const diff = (day + 6) % 7 // Days since Monday
  const weekStart = new Date(utc - diff * 24 * 60 * 60 * 1000)
  const weekEnd = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000)

  const formatDate = (d: Date) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

  return {
    start: formatDate(weekStart),
    end: formatDate(weekEnd),
  }
}

export interface StorybookClassroom {
  id: string;
  name?: string | null;
  grade_level?: string | null;
}

interface WalletBalance {
  balance_credits: number;
}

interface StorybookContextState {
  loading: boolean;
  error: Error | null;
  classroom: StorybookClassroom | null;
  featureEnabled: boolean;
  walletBalance: number;
  storybooksThisWeek: number;
  preferences: Record<string, unknown>;
}

export function useStorybookContext() {
  const { session } = useAuth()
  const teacherId = session?.user?.id ?? null
  const featureFlag = (import.meta.env?.VITE_STORYBOOKS_ENABLED ?? 'true') !== 'false'

  const [state, setState] = useState<StorybookContextState>({
    loading: true,
    error: null,
    classroom: null,
    featureEnabled: true,
    walletBalance: 0,
    storybooksThisWeek: 0,
    preferences: {},
  })

  const loadContext = useCallback(async () => {
    if (!featureFlag) {
      setState((prev) => ({ ...prev, loading: false, error: null, featureEnabled: false }))
      return
    }

    if (!teacherId) {
      setState((prev) => ({ ...prev, loading: false, error: null }))
      return
    }

    setState((prev) => ({ ...prev, loading: true, error: null }))

    try {
      const classroomRes = await supabase
        .from('classrooms')
        .select('id, name, grade_level')
        .eq('teacher_id', teacherId)
        .limit(1)
        .maybeSingle()

      if (classroomRes.error && classroomRes.error.code !== 'PGRST116') {
        throw classroomRes.error
      }

      const classroom = classroomRes.data ?? null
      let featureEnabled = true
      let walletBalance = 0

      if (classroom) {
        const featureRes = await supabase
          .from('classroom_features')
          .select('storybooks_enabled')
          .eq('classroom_id', classroom.id)
          .maybeSingle()

        if (featureRes.error && featureRes.error.code !== 'PGRST116') {
          throw featureRes.error
        }
        featureEnabled = featureRes.data?.storybooks_enabled ?? true
      }

      // Fetch teacher's main wallet balance (Tower Credits)
      const walletRes = await supabase
        .from('credit_accounts')
        .select('balance_credits')
        .eq('user_id', teacherId)
        .eq('kind', 'wallet')
        .maybeSingle()

      if (walletRes.error && walletRes.error.code !== 'PGRST116') {
        throw walletRes.error
      }

      walletBalance = Number(walletRes.data?.balance_credits ?? 0)

      // Count storybooks created this week for free usage tracking
      const weekStart = getWeekStart()
      let storybooksThisWeek = 0

      const countRes = await supabase
        .from('story_books')
        .select('id', { count: 'exact', head: true })
        .eq('teacher_id', teacherId)
        .gte('created_at', weekStart)

      if (!countRes.error) {
        storybooksThisWeek = countRes.count || 0
      }

      const prefRes = await supabase
        .from('profiles')
        .select('storybooks_preferences')
        .eq('id', teacherId)
        .maybeSingle()

      if (prefRes.error && prefRes.error.code !== 'PGRST116') {
        throw prefRes.error
      }

      setState({
        loading: false,
        error: null,
        classroom,
        featureEnabled,
        walletBalance,
        storybooksThisWeek,
        preferences: (prefRes.data?.storybooks_preferences ?? {}) as Record<string, unknown>,
      })
    } catch (error) {
      console.error('[Storybooks] Failed to load context', error)
      setState((prev) => ({ ...prev, loading: false, error: error as Error }))
    }
  }, [featureFlag, teacherId])

  useEffect(() => {
    loadContext()
  }, [loadContext])

  // Is free storybook still available this week?
  const hasFreeAvailable = useMemo(() => {
    return state.storybooksThisWeek < FREE_PER_WEEK
  }, [state.storybooksThisWeek])

  // How many storybooks can be created with current TC balance (plus free if available)
  const storybooksAvailable = useMemo(() => {
    const paidAvailable = Math.floor(state.walletBalance / STORYBOOK_TC_COST)
    return hasFreeAvailable ? paidAvailable + 1 : paidAvailable
  }, [state.walletBalance, hasFreeAvailable])

  // TC balance directly available
  const creditsRemaining = useMemo(() => {
    return state.walletBalance
  }, [state.walletBalance])

  // Cost for next storybook (0 if free available, otherwise 18 TC)
  const nextStorybookCost = useMemo(() => {
    return hasFreeAvailable ? 0 : STORYBOOK_TC_COST
  }, [hasFreeAvailable])

  const updatePreferences = useCallback(
    async (changes: Record<string, unknown>) => {
      if (!teacherId) return null

      const next = { ...(state.preferences || {}), ...changes }
      const response = await supabase
        .from('profiles')
        .update({ storybooks_preferences: next as any })
        .eq('id', teacherId)
        .select('storybooks_preferences')
        .single()

      if (response.error) {
        throw response.error
      }

      const prefs = (response.data?.storybooks_preferences || next) as Record<string, unknown>
      setState((prev) => ({ ...prev, preferences: prefs }))
      return prefs
    },
    [state.preferences, teacherId],
  )

  return {
    ...state,
    enabled: featureFlag,
    teacherId,
    creditsRemaining,
    storybooksAvailable,
    storybookCost: STORYBOOK_TC_COST,
    hasFreeAvailable,
    nextStorybookCost,
    freePerWeek: FREE_PER_WEEK,
    refresh: loadContext,
    updatePreferences,
  }
}
